import { supabase } from '@/lib/supabase';
import { fetchPropBots } from './prop-bot-service';
import { PropBotBatchGrowthService } from './prop-bot-batch-growth-service';
import { PropBot } from '@/types/prop-bots';

/**
 * Optimized service for fetching all prop bots with enhanced stats
 * Replaces N+1 query pattern with batch loading
 */
export class PropBotAggregateService {
  static async fetchAllPropBotsWithStats(userId?: string) {
    try {
      console.log('🚀 [PropBotAggregate] Starting batch fetch...');
      
      // Step 1: Fetch all bots (single query)
      const bots = await fetchPropBots(userId);
      console.log(`✅ [PropBotAggregate] Fetched ${bots?.length || 0} bots`);
      
      // Step 2: Check integration status (single query for all bots)
      const integratedBotIds = new Set();
      if (userId && bots && bots.length > 0) {
        const botIds = bots.map(bot => bot.id);
        
        const { data: botAccounts } = await supabase
          .from('bot_accounts')
          .select('bot_id, original_bot_id')
          .eq('user_id', userId)
          .or(`bot_id.in.(${botIds.join(',')}),original_bot_id.in.(${botIds.join(',')})`);

        botAccounts?.forEach(account => {
          if (botIds.includes(account.bot_id)) {
            integratedBotIds.add(account.bot_id);
          }
          if (account.original_bot_id && botIds.includes(account.original_bot_id)) {
            integratedBotIds.add(account.original_bot_id);
          }
        });
      }

      // Step 3: Batch fetch ALL growth data (2-3 queries total instead of N queries!)
      const botIds = bots!.map(bot => bot.id);
      const growthDataMap = await PropBotBatchGrowthService.getBatchGrowthData(botIds);

      // Step 4: Process in memory (NO MORE DATABASE CALLS!)
      const enhancedBots = bots!.map((bot: PropBot) => {
        const propDetailsObj = Array.isArray(bot.propDetails) && bot.propDetails.length > 0 
          ? bot.propDetails[0] 
          : bot.propDetails;

        const growthData = growthDataMap.get(bot.id);
        
        // Calculate enhanced stats from growth data
        const enhancedStats = this.calculateEnhancedStatsFromGrowth(growthData, propDetailsObj);

        return {
          ...bot,
          enhancedStats,
          is_integrated: integratedBotIds.has(bot.id)
        };
      });

      console.log(`✅ [PropBotAggregate] Successfully processed ${enhancedBots.length} bots with stats`);
      
      return enhancedBots;
    } catch (error) {
      console.error('❌ [PropBotAggregate] Fatal error:', error);
      throw error;
    }
  }

  /**
   * Calculate enhanced stats from growth data (in-memory, no DB calls)
   */
  private static calculateEnhancedStatsFromGrowth(growthData: any, propDetails: any) {
    const parseValue = (value: string | number | undefined): number => {
      if (!value) return 0;
      if (typeof value === 'number') return value;
      return parseFloat(value.toString().replace('%', '')) || 0;
    };

    // Start with manual values as fallback
    const stats = {
      winRate: parseValue(propDetails?.winrate),
      maxDrawdown: parseValue(propDetails?.max_drawdown),
      potentialProfit: parseValue(propDetails?.potential_profit),
      passRate: parseValue(propDetails?.pass_rate),
      estimatedPassTime: parseValue(propDetails?.estimated_pass_time),
      connectedAccountsTotal: propDetails?.connected_accounts_total || 0,
      dataSource: {
        winRate: 'manual' as 'manual' | 'growth_chart',
        maxDrawdown: 'manual' as 'manual' | 'growth_chart',
        potentialProfit: 'manual' as 'manual' | 'growth_chart',
      }
    };

    // Override with growth chart data if available
    if (growthData?.stats && growthData.stats.totalTrades > 0) {
      const minCapital = propDetails?.min_capital || 100000;

      if (growthData.stats.winRate > 0) {
        stats.winRate = Math.round(growthData.stats.winRate * 100) / 100;
        stats.dataSource.winRate = 'growth_chart';
      }

      if (growthData.stats.maxDrawdownDollar && minCapital > 0) {
        stats.maxDrawdown = Math.round((Math.abs(growthData.stats.maxDrawdownDollar) / minCapital) * 10000) / 100;
        stats.dataSource.maxDrawdown = 'growth_chart';
      }

      if (growthData.stats.finalProfit !== undefined && minCapital > 0) {
        const totalProfitPercentage = (growthData.stats.finalProfit / minCapital) * 100;
        stats.potentialProfit = Math.round(totalProfitPercentage * 100) / 100;
        stats.dataSource.potentialProfit = 'growth_chart';
      }
    }

    return stats;
  }
}
